import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowLeft, ChevronLeft, ChevronRight,
} from 'lucide-react';
import { getCompanyLogoWithAuth, getClientCities, resolveCityDataKey } from 'services/companyFileReader';
import {
  Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip, ResponsiveContainer,
} from 'recharts';
import { FullScreenChartModal, ExpandButton } from '../FullScreenChartModal/FullScreenChartModal';
import {
  SERVIZI_FISICI, SERVIZI_DIGITALI, SUB_CATEGORY_ICONS,
} from 'constants/map';
import { getServiziFisiciForCity } from 'utils/ambito';
import { scoreAccent, scoreBarFill } from 'utils/scoreColors';
import YearSelector from '../YearSelector/YearSelector';
import styles from './DrilldownSidebar.module.css';

// ── Helpers ───────────────────────────────────────────────────────────────────

const scoreColor = (s) => scoreAccent(s);
const scoreTrackColor = (s) => scoreBarFill(s);

function DarkRadarTick({ payload, x, y, textAnchor }) {
  const raw = payload?.value ?? '';
  const words = raw.split(' ');
  const lineH = 11;
  if (words.length <= 1) {
    return (
      <text x={x} y={y} textAnchor={textAnchor} dominantBaseline="central"
        fill="rgba(255,255,255,0.7)" fontSize={9.5} fontWeight={600}
        fontFamily="Inter, sans-serif">
        {raw}
      </text>
    );
  }
  const offsetY = -((words.length - 1) * lineH) / 2;
  return (
    <text x={x} y={y} textAnchor={textAnchor}
      fill="rgba(255,255,255,0.7)" fontSize={9.5} fontWeight={600}
      fontFamily="Inter, sans-serif">
      {words.map((word, i) => (
        <tspan key={i} x={x} dy={i === 0 ? offsetY : lineH}>{word}</tspan>
      ))}
    </text>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function GroupLabel({ label, score, onClick, isActive, compareMode }) {
  return (
    <button 
      className={styles.groupLabel} 
      onClick={onClick}
      style={{
        width: '100%',
        background: isActive ? 'rgba(140, 116, 255, 0.15)' : 'transparent',
        border: `1px solid ${isActive ? 'rgba(140, 116, 255, 0.6)' : 'transparent'}`,
        cursor: compareMode ? 'pointer' : 'default',
        padding: '8px',
        borderRadius: '6px',
        margin: '0 -8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}
      disabled={!compareMode}
    >
      <div className={styles.groupLabelText}>{label}</div>
      {score !== null && (
        <span className={styles.groupLabelScore} style={{ color: scoreColor(score) }}>
          {score.toFixed(1)}
        </span>
      )}
    </button>
  );
}

function GroupSeparator() {
  return (
    <div className={styles.separator}>
      <div className={styles.separatorLine} />
      <div className={styles.separatorDot}>✦</div>
      <div className={styles.separatorLine} />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

/**
 * DrilldownSidebar — full analytics sidebar with 2-level navigation,
 * macro categories, sub-categories, breakdown params, and radar chart.
 */
const DrilldownSidebar = ({
  allCompanies,
  selectedYear,
  availableYears: availableYearsProp,
  onYearChange,
  userCompany,
  currentCompany,
  selectedCity,
  onCitySelect,
  activeMacro,
  activeSub,
  activeBreakdown,
  onMacroChange,
  onSubChange,
  onBreakdownChange,
  compareMode,
  isOpen,
  onToggle,
  mapRevealed,
  isSupervisor = false,
  onBackToClients,
}) => {
  const [isRadarExpanded, setIsRadarExpanded] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const logoObjectUrlRef = useRef(null);

  const availableYears = availableYearsProp?.length
    ? availableYearsProp
    : [...new Set((allCompanies || []).map(c => c.anno))].sort((a, b) => b - a);

  useEffect(() => {
    if (!selectedYear) {
      setLogoUrl(null);
      return undefined;
    }

    const logoCompany = userCompany || currentCompany?.azienda;
    if (!logoCompany) {
      setLogoUrl(null);
      return undefined;
    }

    let cancelled = false;

    getCompanyLogoWithAuth(logoCompany, selectedYear)
      .then((url) => {
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (logoObjectUrlRef.current) {
          URL.revokeObjectURL(logoObjectUrlRef.current);
        }
        logoObjectUrlRef.current = url;
        setLogoUrl(url);
      })
      .catch(() => {
        if (!cancelled) {
          setLogoUrl(null);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userCompany, currentCompany?.azienda, selectedYear]);

  useEffect(() => () => {
    if (logoObjectUrlRef.current) {
      URL.revokeObjectURL(logoObjectUrlRef.current);
      logoObjectUrlRef.current = null;
    }
  }, []);

  const cittaObj = currentCompany?.citta || currentCompany?.['città'];
  if (!cittaObj) return null;

  const configuredCities = getClientCities(userCompany || currentCompany?.azienda);
  const cities = configuredCities.length > 0 ? configuredCities : Object.keys(cittaObj);
  const isMultiCity = cities.length > 1;
  const cityName = selectedCity
    ? resolveCityDataKey(cittaObj, selectedCity)
    : resolveCityDataKey(cittaObj, cities[0]) || cities[0] || '';
  const cityData = cittaObj[cityName];
  const isTutteMode = isMultiCity && !selectedCity;

  const getCityScoreForSidebar = (cName) => {
    const cKey = resolveCityDataKey(cittaObj, cName);
    const cd = cittaObj?.[cKey];
    if (!cd) return null;
    // Each city inherits the score from its source JSON during normalization.
    if (typeof cd.indice_sintetico === 'number') return cd.indice_sintetico;
    if (typeof cd['indice sintetico'] === 'number') return cd['indice sintetico'];
    return null;
  };

  let score = currentCompany['indice sintetico'] ?? currentCompany.indice_sintetico ?? 0;
  if (selectedCity) {
    const cScore = getCityScoreForSidebar(selectedCity);
    if (cScore !== null) score = cScore;
  } else if (isTutteMode) {
    let sum = 0, count = 0;
    cities.forEach(c => {
      const cScore = getCityScoreForSidebar(c);
      if (cScore !== null) {
        sum += cScore;
        count++;
      }
    });
    if (count > 0) score = sum / count;
  }

  const serviziFisici = getServiziFisiciForCity(SERVIZI_FISICI, cityData);
  const radarMacros = [...serviziFisici, ...SERVIZI_DIGITALI];
  const radarData = radarMacros.map((b) => ({
    subject: b.label,
    score: typeof cityData?.[b.id]?.punteggio_globale === 'number'
      ? cityData[b.id].punteggio_globale
      : 0,
  }));

  const activeCatData = activeMacro ? cityData?.[activeMacro] : null;
  const allMacrosForCity = [...serviziFisici, ...SERVIZI_DIGITALI];
  const activeMacroMeta = activeMacro ? allMacrosForCity.find((b) => b.id === activeMacro) : null;
  const subs = activeCatData?.sub ? Object.entries(activeCatData.sub) : [];

  // ── Macro row renderer ────────────────────────────────────────────────────
  const renderMacroRow = (btn, idx, overrideCityData, onClickOverride) => {
    const Icon = btn.icon;
    const cd = overrideCityData || cityData;
    const cat = cd?.[btn.id];
    const s = typeof cat?.punteggio_globale === 'number' ? cat.punteggio_globale : null;
    const col = s !== null ? scoreColor(s) : 'rgba(255,255,255,0.5)';
    const btnLabel = btn.label;
    return (
      <motion.button
        key={btn.id}
        initial={{ opacity: 0, x: -10 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: idx * 0.04, type: 'spring', damping: 22, stiffness: 300 }}
        whileHover={{ scale: 1.015, backgroundColor: 'rgba(255,255,255,0.1)' }}
        whileTap={{ scale: 0.97 }}
        onClick={onClickOverride || (() => onMacroChange(btn.id))}
        className={styles.macroBtn}
      >
        <div className={styles.macroBtnIcon} style={{ backgroundColor: btn.color + '20', border: `1px solid ${btn.color}33` }}>
          <Icon size={15} color={btn.color} />
        </div>
        <div className={styles.macroBtnBody}>
          <div className={styles.macroBtnLabel}>{btnLabel}</div>
          <div className={styles.macroBtnTrack}>
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: s !== null ? `${(s / 10) * 100}%` : '0%' }}
              transition={{ duration: 0.5, delay: idx * 0.04 + 0.1 }}
              className={styles.macroBtnBar}
              style={{ background: scoreTrackColor(s) }}
            />
          </div>
        </div>
        {s !== null ? (
          <span className={styles.macroBtnScore} style={{ color: col }}>{s.toFixed(1)}</span>
        ) : (
          <span className={styles.macroBtnNa}>—</span>
        )}
        <ChevronRight size={11} color="rgba(255,255,255,0.5)" style={{ flexShrink: 0 }} />
      </motion.button>
    );
  };

  return (
    <motion.aside 
      className={styles.sidebar}
      initial={{ x: 0 }}
      animate={{ x: isOpen ? 0 : '-100%' }}
      transition={{ type: 'spring', damping: 26, stiffness: 240 }}
    >
      {mapRevealed && (
        <button className={styles.toggleBtn} onClick={onToggle} aria-label="Toggle Sidebar">
          <ChevronRight size={16} style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.3s' }} />
        </button>
      )}

      {/* ── Header ── */}
      <div className={styles.header}>
        {isSupervisor && (
          <button
            type="button"
            className={styles.backToClients}
            onClick={onBackToClients}
          >
            <ArrowLeft size={15} />
            Tutti i clienti
          </button>
        )}
        <div className={styles.logo}>
          <div className={styles.logoIcon}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={currentCompany.azienda}
                className={styles.logoImg}
              />
            ) : (
              <span className={styles.logoFallback}>
                {(currentCompany.azienda || '?')[0]}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '100%' }}>
            <div className={styles.logoName}>{currentCompany.azienda}</div>
            <YearSelector
              years={availableYears}
              value={selectedYear || ''}
              onChange={onYearChange}
              tone="dark"
              compact
              label="Anno dati"
            />
          </div>
        </div>

        <div 
          className={styles.scoreRow}
          onClick={compareMode ? () => onMacroChange(activeMacro === 'indice_sintetico' ? null : 'indice_sintetico') : undefined}
          style={{ 
            cursor: compareMode ? 'pointer' : 'default',
            background: activeMacro === 'indice_sintetico' ? 'rgba(140, 116, 255, 0.15)' : 'transparent',
            border: `1px solid ${activeMacro === 'indice_sintetico' ? 'rgba(140, 116, 255, 0.6)' : 'transparent'}`,
            borderRadius: '6px',
            padding: '4px',
            margin: '0 -4px'
          }}
        >
          <motion.span key={score} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            className={styles.scoreValue}>
            {score.toFixed(2)}
          </motion.span>
          <span className={styles.scoreDenom}>/ 10</span>
        </div>
        <div className={styles.scoreTrack}>
          <motion.div initial={{ width: 0 }} animate={{ width: `${(score / 10) * 100}%` }}
            transition={{ duration: 0.8 }} className={styles.scoreBar} />
        </div>

        {/* City selector */}
        {isMultiCity && (
          <div className={styles.citySelector}>
            <button
              onClick={() => onCitySelect(null)}
              className={styles.cityBtn}
              style={{
                background: !selectedCity ? 'rgba(140, 116, 255, 0.15)' : 'transparent',
                borderColor: !selectedCity ? 'rgba(140, 116, 255, 0.6)' : 'rgba(255,255,255,0.12)',
                color: !selectedCity ? '#a59dff' : 'rgba(255,255,255,0.6)',
              }}
            >
              Tutte
            </button>
            {cities.map((c) => (
              <button
                key={c}
                onClick={() => onCitySelect(c)}
                className={styles.cityBtn}
                style={{
                  background: selectedCity === c ? 'rgba(140, 116, 255, 0.15)' : 'transparent',
                  borderColor: selectedCity === c ? 'rgba(140, 116, 255, 0.6)' : 'rgba(255,255,255,0.12)',
                  color: selectedCity === c ? '#a59dff' : 'rgba(255,255,255,0.6)',
                }}
              >
                {c}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Navigation content ── */}
      <div className={styles.navContent}>
        <AnimatePresence mode="wait">
          {/* L1 — Macro groups */}
          {!activeMacro && (
            <motion.div key={isTutteMode ? 'macros-tutte' : 'macros'}
              initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -20, opacity: 0 }}
              transition={{ type: 'spring', damping: 24, stiffness: 300 }}>
              {isTutteMode ? (
                /* ── Per-city overview ── */
                cities.map((c, ci) => {
                  const cScore = getCityScoreForSidebar(c);
                  const cCol = cScore !== null ? scoreColor(cScore) : 'rgba(255,255,255,0.5)';
                  return (
                    <motion.button
                      key={c}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: ci * 0.04, type: 'spring', damping: 22, stiffness: 300 }}
                      whileHover={{ scale: 1.015, backgroundColor: 'rgba(255,255,255,0.1)' }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => onCitySelect(c)}
                      className={styles.macroBtn}
                      style={{ marginBottom: ci < cities.length - 1 ? 8 : 0, padding: '12px 16px' }}
                    >
                      <div className={styles.macroBtnBody}>
                        <div className={styles.macroBtnLabel} style={{ fontSize: 13, fontWeight: 700, letterSpacing: '.04em' }}>{c}</div>
                        {cScore !== null && (
                          <div className={styles.macroBtnTrack}>
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${(cScore / 10) * 100}%` }}
                              transition={{ duration: 0.5, delay: ci * 0.04 + 0.1 }}
                              className={styles.macroBtnBar}
                              style={{ background: cCol }}
                            />
                          </div>
                        )}
                      </div>
                      {cScore !== null ? (
                        <span className={styles.macroBtnScore} style={{ color: cCol, fontSize: 14 }}>{cScore.toFixed(2)}</span>
                      ) : (
                        <span className={styles.macroBtnNa}>—</span>
                      )}
                      <ChevronRight size={14} color="rgba(255,255,255,0.5)" style={{ flexShrink: 0, marginLeft: 8 }} />
                    </motion.button>
                  );
                })
              ) : (
                /* ── Single city view ── */
                <>
                  <GroupLabel
                    label="SERVIZI FISICI"
                    score={cityData?.punteggio_servizi_fisici ?? null}
                    compareMode={compareMode}
                    isActive={activeMacro === 'servizi_fisici'}
                    onClick={compareMode ? () => onMacroChange(activeMacro === 'servizi_fisici' ? null : 'servizi_fisici') : undefined}
                  />
                  <div className={styles.macroList}>
                    {serviziFisici.map((btn, i) => renderMacroRow(btn, i))}
                  </div>
                  <GroupSeparator />
                  <GroupLabel
                    label="SERVIZI DIGITALI"
                    score={currentCompany.punteggio_servizi_digitali ?? null}
                    compareMode={compareMode}
                    isActive={activeMacro === 'servizi_digitali'}
                    onClick={compareMode ? () => onMacroChange(activeMacro === 'servizi_digitali' ? null : 'servizi_digitali') : undefined}
                  />
                  <div className={styles.macroList}>
                    {SERVIZI_DIGITALI.map((btn, i) => renderMacroRow(btn, i + 3))}
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* L2 — Sub-categories */}
          {activeMacro && !['indice_sintetico', 'servizi_fisici', 'servizi_digitali'].includes(activeMacro) && (
            <motion.div key={`subs-${activeMacro}`}
              initial={{ x: 20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: 20, opacity: 0 }}
              transition={{ type: 'spring', damping: 24, stiffness: 300 }}>
              <button
                onClick={() => { onMacroChange(null); onSubChange(null); }}
                className={styles.backBtn}
              >
                <ChevronLeft size={14} /> AREE DI VALUTAZIONE
              </button>

              {/* Macro header card */}
              <div className={styles.macroCard} style={{
                backgroundColor: 'rgba(140, 116, 255, 0.15)',
                borderColor: 'rgba(140, 116, 255, 0.6)',
              }}>
                <div className={styles.macroCardHeader}>
                  {activeMacroMeta && (
                    <div className={styles.macroCardIcon} style={{ backgroundColor: activeMacroMeta.color + '20' }}>
                      <activeMacroMeta.icon size={14} color={activeMacroMeta.color} />
                    </div>
                  )}
                  <div className={styles.macroCardInfo}>
                    <div className={styles.macroCardTag}>MACROCATEGORIA</div>
                    <div className={styles.macroCardName}>
                      {activeMacroMeta?.label?.toUpperCase()}
                    </div>
                  </div>
                  {typeof activeCatData?.punteggio_globale === 'number' && (
                    <span className={styles.macroCardScore}
                      style={{ color: scoreColor(activeCatData.punteggio_globale) }}>
                      {activeCatData.punteggio_globale.toFixed(1)}
                    </span>
                  )}
                </div>
                {typeof activeCatData?.punteggio_globale === 'number' && (
                  <div className={styles.macroCardTrack}>
                    <motion.div initial={{ width: 0 }}
                      animate={{ width: `${(activeCatData.punteggio_globale / 10) * 100}%` }}
                      transition={{ duration: 0.6 }}
                      className={styles.macroCardBar}
                      style={{ background: scoreTrackColor(activeCatData.punteggio_globale) }} />
                  </div>
                )}
              </div>

              {/* Sub-category list */}
              {subs.length === 0 ? (
                <div className={styles.emptyState}>NESSUNA SOTTOCATEGORIA</div>
              ) : (
                <>
                  <div className={styles.subsHint}>
                    {compareMode ? 'CATEGORIE · SELEZIONA PER CONFRONTARE' : 'CATEGORIE · CLICCA PER I GRAFICI'}
                  </div>
                  <div className={styles.subsList}>
                    {subs.map(([subKey, subData], i) => {
                      const subScore = typeof subData?.punteggio_globale === 'number'
                        ? subData.punteggio_globale : null;
                      const subCol = subScore !== null ? scoreColor(subScore) : '#e5e5cc';
                      const isActive = activeSub === subKey;
                      const SubIcon = SUB_CATEGORY_ICONS[subKey] || ChevronRight;
                      const bdItems = isActive
                        ? Object.entries(subData?.breakdown || {})
                        : [];

                      return (
                        <div key={subKey}>
                          <motion.button
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.04, type: 'spring', damping: 22, stiffness: 300 }}
                            whileHover={{ scale: 1.015 }}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => onSubChange(isActive ? null : subKey)}
                            className={styles.subBtn}
                            style={{
                              backgroundColor: isActive ? 'rgba(140, 116, 255, 0.15)' : 'rgba(255,255,255,0.05)',
                              borderColor: isActive
                                ? 'rgba(140, 116, 255, 0.6)'
                                : (activeMacroMeta ? activeMacroMeta.color + '18' : 'rgba(255,255,255,0.08)'),
                            }}
                          >
                            <div className={styles.subBtnIcon}>
                              <SubIcon size={12} color="rgba(255,255,255,0.7)" />
                            </div>
                            <div className={styles.subBtnBody}>
                              <div className={styles.subBtnLabel}>
                                {subKey.replace(/_/g, ' ')}
                              </div>
                              {subScore !== null && (
                                <div className={styles.subBtnTrack}>
                                  <motion.div initial={{ width: 0 }}
                                    animate={{ width: `${(subScore / 10) * 100}%` }}
                                    transition={{ duration: 0.4, delay: i * 0.04 }}
                                    className={styles.subBtnBar}
                                    style={{ background: subCol }} />
                                </div>
                              )}
                            </div>
                            {subScore !== null && (
                              <span className={styles.subBtnScore} style={{ color: subCol }}>
                                {subScore.toFixed(1)}
                              </span>
                            )}
                            <ChevronRight
                              size={11}
                              color={isActive ? '#a59dff' : 'rgba(255,255,255,0.5)'}
                              style={{ flexShrink: 0, transform: isActive ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                            />
                          </motion.button>

                          {/* Breakdown params */}
                          <AnimatePresence>
                            {isActive && bdItems.length > 0 && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.2 }}
                                className={styles.breakdown}
                              >
                                <div className={styles.breakdownLabel}>PARAMETRI</div>
                                <div className={styles.breakdownGrid}>
                                  {bdItems.map(([bdKey, bdVal]) => {
                                    const bdScore = typeof bdVal === 'number' ? bdVal : null;
                                    const bdColor = bdScore !== null ? scoreColor(bdScore) : 'rgba(255,255,255,0.5)';
                                    const isActiveBd = activeBreakdown === bdKey && compareMode;
                                    return (
                                      <button key={bdKey}
                                        onClick={() => compareMode
                                          ? onBreakdownChange(isActiveBd ? null : bdKey)
                                          : undefined}
                                        className={styles.bdBtn}
                                        style={{
                                          cursor: compareMode ? 'pointer' : 'default',
                                          background: isActiveBd ? 'rgba(140, 116, 255, 0.15)' : 'rgba(255,255,255,0.03)',
                                          border: `1px solid ${isActiveBd ? 'rgba(140, 116, 255, 0.6)' : 'rgba(255,255,255,0.06)'}`,
                                        }}
                                      >
                                        <span className={styles.bdKey}>{bdKey.replace(/_/g, ' ')}</span>
                                        <span className={styles.bdScore} style={{ color: bdColor }}>
                                          {bdScore !== null ? bdScore.toFixed(1) : '—'}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Radar chart ── */}
      {!compareMode && !isTutteMode && (
        <div className={styles.radarSection} style={{ position: 'relative' }}>
          <div className={styles.radarTitle}>RAGNATELA GLOBALE</div>
          <ExpandButton onClick={() => setIsRadarExpanded(true)} />
          <ResponsiveContainer width="100%" height={170}>
            <RadarChart data={radarData} margin={{ top: 8, right: 24, bottom: 8, left: 24 }}>
              <PolarGrid stroke="rgba(255,255,255,0.1)" strokeDasharray="3 3" />
              <PolarAngleAxis dataKey="subject" tick={<DarkRadarTick />} />
              <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
              <Radar dataKey="score" stroke="#A3E635" fill="#A3E635" fillOpacity={0.18} strokeWidth={1.5}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  return (
                    <circle key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={3.5}
                      fill={scoreColor(payload.score)}
                      stroke="rgba(255,255,255,0.8)" strokeWidth={1} />
                  );
                }}
              />
              <Tooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const { subject, score: s } = payload[0].payload;
                return (
                  <div className={styles.radarTooltip}>
                    <div className={styles.radarTooltipLabel}>{subject}</div>
                    <div className={styles.radarTooltipScore} style={{ color: scoreColor(s) }}>
                      {s?.toFixed(1) ?? '—'}<span className={styles.radarTooltipDenom}>/10</span>
                    </div>
                  </div>
                );
              }} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      )}

      <AnimatePresence>
        {isRadarExpanded && (
          <FullScreenChartModal
            isOpen={true}
            title="Ragnatela Globale"
            onClose={() => setIsRadarExpanded(false)}
          >
            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyItems: 'center' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} margin={{ top: 20, right: 40, bottom: 20, left: 40 }}>
                  <PolarGrid stroke="rgba(0,0,0,0.1)" strokeDasharray="3 3" />
                  <PolarAngleAxis dataKey="subject" tick={{ fill: '#353A0A', fontSize: 14, fontWeight: 600 }} />
                  <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
                  <Radar dataKey="score" stroke="#A3E635" fill="#A3E635" fillOpacity={0.25} strokeWidth={3}
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      return (
                        <circle key={`dot-${cx}-${cy}`} cx={cx} cy={cy} r={6}
                          fill={scoreColor(payload.score)}
                          stroke="rgba(255,255,255,0.8)" strokeWidth={2} />
                      );
                    }}
                  />
                  <Tooltip content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const { subject, score: s } = payload[0].payload;
                    return (
                      <div className={styles.radarTooltip} style={{ background: '#353A0A', padding: '12px', borderRadius: '8px', color: '#fff' }}>
                        <div className={styles.radarTooltipLabel} style={{ fontSize: '14px', marginBottom: '4px' }}>{subject}</div>
                        <div className={styles.radarTooltipScore} style={{ color: scoreColor(s), fontSize: '18px', fontWeight: 700 }}>
                          {s?.toFixed(1) ?? '—'}<span className={styles.radarTooltipDenom} style={{ fontSize: '12px', opacity: 0.7 }}>/10</span>
                        </div>
                      </div>
                    );
                  }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </FullScreenChartModal>
        )}
      </AnimatePresence>

    </motion.aside>
  );
};

export default DrilldownSidebar;
